// Other players on the server: the ET character (PlayerModel) with a name
// tag, interpolated between snapshots with a small delay for smooth motion.
// The animation events and movetypes of the snapshots are played at the same
// delay. Without the player model bundle they are boxes.

import * as THREE from 'three';
import type { RemotePlayer } from '../shared/net/protocol';
import { ANIM_EVENT } from '../shared/game/movetype';
import type { Vec3 } from '../shared/math';
import { PlayerModel, type PlayerRenderKit } from './playerModel';

const INTERP_DELAY = 100; // ms
/** A jump between two snapshots longer than this is a teleport: not interpolated. */
const TELEPORT_DIST = 128;

interface Sample {
  time: number;
  origin: Vec3;
  angles: Vec3;
  velocity: Vec3;
  crouched: boolean;
  prone: boolean;
  moveType: number;
  moveFallback: number;
  movementDir: number;
  animEvent: number;
  animEventSeq: number;
}

interface Entry {
  id: number;
  group: THREE.Group;
  /** Fallback box (no player model). */
  body: THREE.Mesh | null;
  model: PlayerModel | null;
  label: THREE.Sprite;
  samples: Sample[];
  name: string;
  running: string | null;
  noclip: boolean;
  /** Last animation event played, and the time of the newest sample looked at. */
  eventSeq: number;
  eventTime: number;
  lastRender: number;
}

/** AngleSubtract */
function angleSubtract(a: number, b: number): number {
  let d = a - b;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}

/** Plays a snapshot's animation event on a model. */
export function playAnimEvent(model: PlayerModel, event: number): void {
  const a = model.animator;
  if (event === ANIM_EVENT.JUMP || event === ANIM_EVENT.JUMPBK) a.jump(event === ANIM_EVENT.JUMPBK, true);
  else if (event === ANIM_EVENT.KICKOFF || event === ANIM_EVENT.KICKOFF_BK) a.jump(event === ANIM_EVENT.KICKOFF_BK, false);
  else if (event === ANIM_EVENT.LAND) a.land();
}

export class RemotePlayers {
  readonly object = new THREE.Group();
  private players = new Map<number, Entry>();
  private bodyGeo = new THREE.BoxGeometry(36, 36, 72);
  private bodyMat = new THREE.MeshBasicMaterial({ color: 0xffa040, transparent: true, opacity: 0.45, depthWrite: false });
  private edgeGeo = new THREE.EdgesGeometry(this.bodyGeo);
  private edgeMat = new THREE.LineBasicMaterial({ color: 0xffc070 });
  /** A player not drawn (the one spectated in first person). */
  hidden: number | null = null;
  /** Every drawn frame of a visible player: where it is and its movetype (footstep sounds). */
  onFrame: ((id: number, origin: Vec3, moveType: number, frametime: number, now: number) => void) | null = null;
  /** An animation event of a player was played (landing sounds). */
  onEvent: ((origin: Vec3, event: number) => void) | null = null;

  constructor(private readonly kit: PlayerRenderKit | null = null) {}

  update(players: RemotePlayer[], now: number): void {
    const seen = new Set<number>();
    for (const p of players) {
      seen.add(p.id);
      let e = this.players.get(p.id);
      if (!e) {
        e = this.create(p);
        this.players.set(p.id, e);
      }
      if (e.name !== p.name || e.running !== p.running) this.setLabel(e, p.name, p.running);
      e.samples.push({
        time: now,
        origin: [...p.origin],
        angles: [...p.viewangles],
        velocity: [...p.velocity],
        crouched: p.crouched,
        prone: p.prone,
        moveType: p.moveType ?? 0,
        moveFallback: p.moveFallback ?? 0,
        movementDir: p.movementDir ?? 0,
        animEvent: p.animEvent ?? 0,
        animEventSeq: p.animEventSeq ?? 0,
      });
      if (e.samples.length > 20) e.samples.shift();
      e.noclip = p.noclip;
    }
    for (const [id, e] of this.players) {
      if (seen.has(id)) continue;
      this.object.remove(e.group);
      e.model?.dispose();
      (e.label.material as THREE.SpriteMaterial).map?.dispose();
      e.label.material.dispose();
      this.players.delete(id);
    }
  }

  /** ClientNumberFromString: a player by id or by (a part of) the name. */
  find(query: string): { id: number; name: string } | null {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const all = [...this.players.values()];
    const byId = /^\d+$/.test(q) ? all.find((e) => e.id === Number(q)) : undefined;
    const exact = all.find((e) => e.name.toLowerCase() === q);
    const partial = all.filter((e) => e.name.toLowerCase().includes(q));
    const e = byId ?? exact ?? (partial.length === 1 ? partial[0] : undefined);
    return e ? { id: e.id, name: e.name } : null;
  }

  get names(): { name: string; running: string | null }[] {
    return [...this.players.values()].map((e) => ({ name: e.name, running: e.running }));
  }

  render(now: number, viewOrigin: ArrayLike<number>): void {
    const t = now - INTERP_DELAY;
    for (const e of this.players.values()) {
      const s = e.samples;
      e.group.visible = !e.noclip && e.id !== this.hidden;
      if (!s.length) continue;
      let ai = 0;
      let bi = 0;
      for (let i = 0; i < s.length; i++) {
        if (s[i].time <= t) ai = i;
        bi = i;
        if (s[i].time >= t) break;
      }
      const a = s[ai];
      const b = s[bi];
      const jump = Math.hypot(b.origin[0] - a.origin[0], b.origin[1] - a.origin[1], b.origin[2] - a.origin[2]);
      let k = b.time > a.time ? Math.min(1, Math.max(0, (t - a.time) / (b.time - a.time))) : 1;
      if (jump > TELEPORT_DIST) k = k < 1 ? 0 : 1;
      const o: Vec3 = [a.origin[0] + (b.origin[0] - a.origin[0]) * k, a.origin[1] + (b.origin[1] - a.origin[1]) * k, a.origin[2] + (b.origin[2] - a.origin[2]) * k];
      e.group.position.set(o[0], o[1], o[2]);
      if (e.body) {
        const height = b.prone ? 40 : b.crouched ? 48 : 72;
        e.body.scale.set(1, 1, height / 72);
        e.body.position.set(0, 0, -24 + height / 2);
      }
      if (!e.group.visible) continue;
      const frametime = e.lastRender ? Math.max(0, now - e.lastRender) : 0;
      e.lastRender = now;
      // the game side of the animation, at the drawn moment
      e.model?.animator.advanceTimers(frametime);
      for (let i = 0; i <= ai; i++) {
        if (s[i].time <= e.eventTime) continue;
        e.eventTime = s[i].time;
        if (s[i].animEventSeq === e.eventSeq) continue;
        e.eventSeq = s[i].animEventSeq;
        if (e.model) playAnimEvent(e.model, s[i].animEvent);
        this.onEvent?.(o, s[i].animEvent);
      }
      this.onFrame?.(e.id, o, a.moveType, frametime, now);
      if (e.model) {
        const model = e.model;
        model.animator.locomotion(a.moveType, a.moveFallback);
        const angles: Vec3 = [0, 0, 0];
        for (let j = 0; j < 3; j++) angles[j] = a.angles[j] + angleSubtract(b.angles[j], a.angles[j]) * k;
        // the model is placed in world space, the group carries only the label
        model.object.position.set(-o[0], -o[1], -o[2]);
        model.render({
          time: t,
          frametime,
          origin: o,
          angles,
          velocity: k < 0.5 ? a.velocity : b.velocity,
          movementDir: a.movementDir,
          prone: a.prone,
          crouched: a.crouched,
          snapshotTime: a.time + 1,
          viewOrigin,
        });
      }
    }
  }

  private create(p: RemotePlayer): Entry {
    const group = new THREE.Group();
    let body: THREE.Mesh | null = null;
    let model: PlayerModel | null = null;
    if (this.kit) {
      model = new PlayerModel(this.kit, p.id);
      group.add(model.object);
    } else {
      body = new THREE.Mesh(this.bodyGeo, this.bodyMat);
      body.add(new THREE.LineSegments(this.edgeGeo, this.edgeMat));
      group.add(body);
    }
    // constant on-screen size, readable at any distance
    const label = new THREE.Sprite(new THREE.SpriteMaterial({ depthTest: false, transparent: true, sizeAttenuation: false }));
    label.position.set(0, 0, 70);
    label.renderOrder = 10;
    group.add(label);
    this.object.add(group);
    const e: Entry = { id: p.id, group, body, model, label, samples: [], name: '', running: null, eventSeq: p.animEventSeq ?? 0, eventTime: -Infinity, lastRender: 0, noclip: false };
    this.setLabel(e, p.name, p.running);
    return e;
  }

  private setLabel(e: Entry, name: string, running: string | null): void {
    e.name = name;
    e.running = running;
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.font = 'bold 30px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, 8, 256, 44);
    ctx.fillStyle = running ? '#9ff0a8' : '#ffffff';
    ctx.fillText(running ? `${name} ⏱` : name, 128, 42);
    const mat = e.label.material as THREE.SpriteMaterial;
    mat.map?.dispose();
    mat.map = new THREE.CanvasTexture(canvas);
    mat.needsUpdate = true;
    e.label.scale.set(0.16, 0.04, 1);
  }

  dispose(): void {
    for (const e of this.players.values()) {
      e.model?.dispose();
      (e.label.material as THREE.SpriteMaterial).map?.dispose();
      e.label.material.dispose();
    }
    this.players.clear();
    this.bodyGeo.dispose();
    this.bodyMat.dispose();
    this.edgeGeo.dispose();
    this.edgeMat.dispose();
  }
}
